import { http, unwrapData, type ApiLocale } from '@noor/api';
import type * as z from 'zod';
import {
  listResponseSchema,
  orderListResponseSchema,
  type ListResult,
  type OrderListResult,
  type PaginationParams,
} from '../model';

// Thin per-slice HTTP helpers over `@noor/api`. Two envelope shapes to unwrap
// (see `model/common.ts`):
//   - list:   { list: [...], data: {}, meta } → parsed to `{ list, meta }`
//   - single: { data: <payload>, meta }       → `data` parsed against the schema
//
// The configured base URL already carries the `/api/v1` prefix, so paths here
// are relative (`merchant/restaurants`, `merchant/product-categories`, …).
//
// Every helper parses the response against a Zod schema, so contract drift
// fails loudly at the boundary rather than silently downstream.

/** Optional per-request cancellation, forwarded to `fetch`. */
export interface RequestOptions {
  signal?: AbortSignal;
  /** Override the default API base for services mounted on another gateway. */
  baseURL?: string;
  /**
   * Override the `x-locale` header for this one request, instead of using the
   * app's active language. Needed to read the SAME resource in several
   * languages at once — the merchant editor does this to recover a record's
   * translations, since no read RPC returns them all (see the app's CLAUDE.md).
   */
  locale?: ApiLocale;
}

/** Query params for a list endpoint: pagination plus arbitrary filters. */
export type ListQuery = PaginationParams & Record<string, unknown>;

/**
 * Spread the per-request ofetch options a caller supplied — cancellation, and
 * a locale override that beats the client's global `x-locale` interceptor.
 */
function requestOptions(options?: RequestOptions):
  | {
      signal?: AbortSignal;
      baseURL?: string;
      headers?: Record<string, string>;
    }
  | undefined {
  if (!options?.signal && !options?.baseURL && !options?.locale) return undefined;

  return {
    ...(options.signal ? { signal: options.signal } : {}),
    ...(options.baseURL ? { baseURL: options.baseURL } : {}),
    ...(options.locale ? { headers: { 'x-locale': options.locale } } : {}),
  };
}

/**
 * Split a caller query into ofetch `query` params, mapping `limit`/`offset`
 * onto the backend's nested `pagination.limit` / `pagination.offset` keys,
 * dropping `undefined` values, and expanding array values into repeated query
 * parameters (e.g. `product_ids: ['a', 'b']` becomes `product_ids=a&product_ids=b`).
 */
export function toPaginationQuery(query: ListQuery = {}): Record<string, unknown> {
  const { limit, offset, ...rest } = query;
  const params: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined) continue;
    // Keep arrays as-is; ofetch will expand them into repeated params
    params[key] = value;
  }
  if (limit !== undefined) params['pagination.limit'] = limit;
  if (offset !== undefined) params['pagination.offset'] = offset;
  return params;
}

/**
 * Drop `undefined` entries from a flat query object and expand array values
 * into repeated query parameters (e.g. `product_ids: ['a', 'b']` becomes
 * `product_ids=a&product_ids=b`). This is required for the API's array params,
 * which do not accept a single stringified array.
 */
export function toQuery(query: Record<string, unknown> = {}): Record<string, string[]> {
  const params: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    const values = (Array.isArray(value) ? value : [value]).flatMap(toQueryValue);
    // An array that held nothing usable contributes no key at all, rather than
    // an empty one the gateway would read as a real (blank) filter.
    if (values.length > 0) params[key] = values;
  }
  return params;
}

/**
 * One query value as a string, or nothing when it cannot be represented.
 *
 * Query params are primitives by definition — an object stringifies to
 * `[object Object]`, which the gateway would receive as a literal filter value
 * — so a non-primitive is DROPPED rather than silently sent as garbage.
 */
function toQueryValue(value: unknown): string[] {
  if (value === undefined || value === null) return [];
  switch (typeof value) {
    case 'string':
      return [value];
    case 'number':
    case 'boolean':
    case 'bigint':
      return [String(value)];
    default:
      return [];
  }
}

/** GET a list endpoint and parse its `{ list, meta }` envelope. */
export async function getList<Item extends z.ZodType>(
  url: string,
  item: Item,
  query?: ListQuery,
  options?: RequestOptions,
): Promise<ListResult<z.infer<Item>>> {
  const raw = await http.get<unknown>(url, {
    query: toPaginationQuery(query),
    ...requestOptions(options),
  });
  return listResponseSchema(item).parse(raw);
}

/** GET the order list endpoint and parse its `{ list, meta, data: { counts } }` envelope. */
export async function getOrderList(
  url: string,
  query?: ListQuery,
  options?: RequestOptions,
): Promise<OrderListResult> {
  const raw = await http.get<unknown>(url, {
    query: toPaginationQuery(query),
    ...requestOptions(options),
  });
  return orderListResponseSchema.parse(raw);
}

/**
 * GET a list endpoint whose query params are flat (no `pagination.*` nesting) —
 * e.g. `merchant/products/top`, which takes a bare `limit`, and the weekday-count
 * endpoints, which take only a date range.
 */
export async function getFlatList<Item extends z.ZodType>(
  url: string,
  item: Item,
  query?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<ListResult<z.infer<Item>>> {
  const raw = await http.get<unknown>(url, {
    query: toQuery(query),
    ...requestOptions(options),
  });
  return listResponseSchema(item).parse(raw);
}

/** GET a single resource, unwrap `data`, and parse it against `schema`. */
export async function getOne<Schema extends z.ZodType>(
  url: string,
  schema: Schema,
  query?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<z.infer<Schema>> {
  const raw = await http.get<unknown>(url, {
    query: toQuery(query),
    ...requestOptions(options),
  });
  return schema.parse(unwrapData(raw));
}

/**
 * GET a single resource whose envelope carries a sibling top-level `list`
 * alongside `data` (e.g. a branch plus its weekly schedule). `schema` parses
 * the raw envelope directly — `unwrapData` would discard `list`.
 */
export async function getOneWithList<Schema extends z.ZodType>(
  url: string,
  schema: Schema,
  query?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<z.infer<Schema>> {
  const raw = await http.get<unknown>(url, {
    query: toQuery(query),
    ...requestOptions(options),
  });
  return schema.parse(raw);
}

/** POST a body, unwrap `data`, and parse the result against `schema`. */
export async function postOne<Schema extends z.ZodType>(
  url: string,
  body: unknown,
  schema: Schema,
  options?: RequestOptions,
): Promise<z.infer<Schema>> {
  const raw = await http.post<unknown>(url, body, requestOptions(options));
  return schema.parse(unwrapData(raw));
}

/**
 * POST a body to an endpoint whose response carries no payload worth parsing
 * (the gateway answers with an empty `data` object).
 */
export async function postVoid(url: string, body: unknown): Promise<void> {
  await http.post<unknown>(url, body);
}

/**
 * POST a body to an endpoint whose response carries a sibling top-level `list`
 * alongside `data` (e.g. creating a product returns the product in `data` and
 * its per-branch overlays in `list`). `schema` parses the raw envelope
 * directly — `unwrapData` would discard `list`.
 */
export async function postOneWithList<Schema extends z.ZodType>(
  url: string,
  body: unknown,
  schema: Schema,
): Promise<z.infer<Schema>> {
  const raw = await http.post<unknown>(url, body);
  return schema.parse(raw);
}

/** POST a body and parse the `{ list, meta }` envelope it returns. */
export async function postList<Item extends z.ZodType>(
  url: string,
  body: unknown,
  item: Item,
): Promise<ListResult<z.infer<Item>>> {
  const raw = await http.post<unknown>(url, body);
  return listResponseSchema(item).parse(raw);
}

/** PATCH a body, unwrap `data`, and parse the result against `schema`. */
export async function patchOne<Schema extends z.ZodType>(
  url: string,
  body: unknown,
  schema: Schema,
  options?: RequestOptions,
): Promise<z.infer<Schema>> {
  const fetchOptions = requestOptions(options);
  const raw = fetchOptions
    ? await http.patch<unknown>(url, body, fetchOptions)
    : await http.patch<unknown>(url, body);
  return schema.parse(unwrapData(raw));
}

/**
 * PATCH a body to an endpoint whose response carries a sibling top-level `list`
 * alongside `data` (e.g. the branch update, which answers with the restaurant
 * or a moderation receipt in `data` and the saved 7-day schedule in `list`).
 * `schema` parses the raw envelope directly — `unwrapData` would discard `list`.
 */
export async function patchOneWithList<Schema extends z.ZodType>(
  url: string,
  body: unknown,
  schema: Schema,
): Promise<z.infer<Schema>> {
  const raw = await http.patch<unknown>(url, body);
  return schema.parse(raw);
}

/** PATCH a body and parse the `{ list, meta }` envelope it returns. */
export async function patchList<Item extends z.ZodType>(
  url: string,
  body: unknown,
  item: Item,
): Promise<ListResult<z.infer<Item>>> {
  const raw = await http.patch<unknown>(url, body);
  return listResponseSchema(item).parse(raw);
}

/** PUT a body, unwrap `data`, and parse the result against `schema`. */
export async function putOne<Schema extends z.ZodType>(
  url: string,
  body: unknown,
  schema: Schema,
): Promise<z.infer<Schema>> {
  const raw = await http.put<unknown>(url, body);
  return schema.parse(unwrapData(raw));
}

/**
 * PUT a body to an endpoint whose response carries no payload worth parsing
 * (e.g. the image reorder, which answers with the updated product the caller
 * refetches anyway). The `postVoid` counterpart.
 */
export async function putVoid(url: string, body: unknown): Promise<void> {
  await http.put<unknown>(url, body);
}

/** PUT a body and parse the `{ list, meta }` envelope it returns. */
export async function putList<Item extends z.ZodType>(
  url: string,
  body: unknown,
  item: Item,
): Promise<ListResult<z.infer<Item>>> {
  const raw = await http.put<unknown>(url, body);
  return listResponseSchema(item).parse(raw);
}

/**
 * DELETE a resource whose response body carries nothing worth returning
 * (most deletes answer with an empty `data` object).
 */
export async function deleteOne(
  url: string,
  query?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<void> {
  await http.delete<unknown>(url, { query: toQuery(query), ...requestOptions(options) });
}

/**
 * DELETE a resource, sending the ids to remove in the request BODY.
 *
 * Not every delete addresses its target by path alone.
 * `DELETE /merchant/products/{id}/restaurant-products` takes a `restaurant_ids` array,
 * and the spec marks its `requestBody` **required** — so the list has to go in
 * the body, not the query string. Sending it as a query param is what made an
 * ordinary product edit fail with `Invalid input`: the route saw a body-less
 * DELETE and rejected it before ever looking at the query.
 */
export async function deleteWithBody(
  url: string,
  body: Record<string, unknown>,
): Promise<void> {
  await http.delete<unknown>(url, { body });
}

/**
 * DELETE a resource and parse its `data` payload — for the deletes that are
 * NOT fire-and-forget: a moderation-gated delete (e.g. promotions) answers
 * with a `pending_change` receipt, and swallowing it would report a queued
 * delete as done.
 */
export async function deleteOneParsed<Schema extends z.ZodType>(
  url: string,
  schema: Schema,
  query?: Record<string, unknown>,
): Promise<z.infer<Schema>> {
  const raw = await http.delete<unknown>(url, { query: toQuery(query) });
  return schema.parse(unwrapData(raw));
}

/**
 * DELETE with a request BODY whose response also needs parsing — the two
 * behaviours {@link deleteWithBody} and {@link deleteOneParsed} have one each.
 *
 * `DELETE /merchant/promotions/{id}/products` needs both: it moved from query params to
 * a JSON body, and like every promotion write it is moderation-gated, so the
 * response may be a `pending_change` receipt that must not be swallowed.
 */
export async function deleteWithBodyParsed<Schema extends z.ZodType>(
  url: string,
  schema: Schema,
  body: Record<string, unknown>,
): Promise<z.infer<Schema>> {
  const raw = await http.delete<unknown>(url, { body });
  return schema.parse(unwrapData(raw));
}
